import type { LinearRGB } from '../../core/color';
import type { Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import type { ShipTemplate, MastConfig } from './shipTemplates';
import { stationAt, gunwaleHalfWidth, bowTopHeight, type HullStation } from './hull';
import { sparSolid } from './shipSolids';

/* Painter-view widening of thin parts, so masts, yards and ropes still read at ~2.8 km. */
export const MAST_PAINTER_WIDTH_SCALE = 1.5;
export const YARD_PAINTER_WIDTH_SCALE = 2.5;
export const ROPE_PAINTER_WIDTH_SCALE = 6.0;

/* Set sails are braced 35-40 degrees from athwartships, so the painter camera, abeam of every
   anchored ship, sees at least sin 35 degrees of each sail's face instead of its edge. A yard
   braces up only until it meets the lee shrouds: at 40 degrees the main shrouds come down 12-21
   degrees abaft the mast, near R3's 0-19 (scan 280,4440-700,4810). */
export const BRACE = { min: (35 * Math.PI) / 180, max: (40 * Math.PI) / 180 } as const;

/** Beams a rig spans across as ships are placed: a sprit rig's sails sheeted out furthest, and a
    formed ship's furled course yards as the painter view draws them (shipFootprint.ts). */
export const RIG_SPAN = 1.2;

export const MAST = { radius: 0.3, taper: 0.7, sides: 6, step: 0.5 } as const;
/** No mast is thicker than this at its foot, nor than 1.5 times it in the painter view. */
export const MAST_RADIUS = MAST.radius;
const ROPE = { halfWidth: 0.04, sides: 4 } as const;
/** How far the surface of a rope stands off its axis at most. */
export const ROPE_RADIUS = ROPE.halfWidth / Math.cos(Math.PI / ROPE.sides);
const SHROUD = {
  minAft: 0.4,
  step: 0.05,
  spread: 0.8,
  head: 0.85,
  drop: 0.25,
  sink: 0.25,
  inboard: 0.9,
} as const;
const FORESTAY = { bowInset: 0.4, sink: 0.3 } as const;
/** A pennant staff: its radius and how far it stands in the masthead. */
export const STAFF = { radius: 0.06, footing: 0.3 } as const;
/** How far a pennant staff stands above the masthead of a square- or lateen-rigged ship. */
export const STAFF_HEIGHT = 1.6;
export const YARD = { radius: 0.08, sides: 6 } as const;
/* The big ships' bowsprit reaches a fifth of the length out from its heel, just inside the stem,
   rising 25 degrees; its forestay comes down 0.85 of the way out. */
export const BOWSPRIT = {
  reach: 0.2,
  rise: (25 * Math.PI) / 180,
  radius: 0.14,
  taper: 0.6,
  heelInset: 0.8,
  stay: 0.85,
} as const;

export interface RigStyles {
  readonly spar: VertexStyle;
  readonly rope: VertexStyle;
  readonly sail: VertexStyle;
}

/** A mast where it is stepped: its axis at `x`, standing `height` above the deck at its foot. */
export interface Mast {
  readonly x: number;
  readonly deck: number;
  readonly height: number;
  readonly step: number;
}

export function rigStylesFor(
  riggingColor: LinearRGB,
  sailColor: LinearRGB,
  edgeId: number,
  seed: number
): RigStyles {
  const base = { edgeId, seed, extent: [0, 0] as const };
  return {
    spar: { ...base, color: riggingColor, surface: SURFACE.wood, strokeAngle: Math.PI / 2 },
    rope: { ...base, color: riggingColor, surface: SURFACE.linework, strokeAngle: 0 },
    sail: { ...base, color: sailColor, surface: SURFACE.sail, strokeAngle: 0 },
  };
}

export function stationAtX(
  stations: readonly HullStation[],
  x: number,
  length: number
): HullStation {
  return stationAt(stations, x / length + 0.5);
}

export function mastAt(stations: readonly HullStation[], config: MastConfig): Mast {
  const station = stationAt(stations, config.position);
  const deck = station.gunwale;
  return { x: station.x, deck, height: config.height, step: deck - MAST.step };
}

/** The radius of a mast at height `y`: it tapers from its step to its head. */
export function mastRadiusAt(mast: Mast, y: number): number {
  const top = mast.deck + mast.height;
  return MAST.radius * (1 - (1 - MAST.taper) * ((y - mast.step) / (top - mast.step)));
}

/*
 * Each masthead carries a thin staff for its pennant, as the painting's ships do. Lifting the
 * pennant above the masthead also keeps it clear of the next mast's yard: a fore mast here is
 * about 0.7 of the main mast's height, where the main yard hangs.
 */
export function staffTopOf(mast: Mast, staffHeight: number): Vec3 {
  return [mast.x, mast.deck + mast.height + staffHeight, 0];
}

/** The mast from its step to its head, and the pennant staff standing on the head. */
export function buildMast(
  builder: MeshBuilder,
  style: VertexStyle,
  mast: Mast,
  staffHeight: number
): void {
  const top = mast.deck + mast.height;
  sparSolid(builder, style, {
    from: [mast.x, mast.step, 0],
    to: [mast.x, top, 0],
    radiusFrom: MAST.radius,
    radiusTo: MAST.radius * MAST.taper,
    sides: MAST.sides,
    painterWidthScale: MAST_PAINTER_WIDTH_SCALE,
  });
  sparSolid(builder, style, {
    from: [mast.x, top - STAFF.footing, 0],
    to: staffTopOf(mast, staffHeight),
    radiusFrom: STAFF.radius,
    radiusTo: STAFF.radius,
    sides: MAST.sides,
    painterWidthScale: MAST_PAINTER_WIDTH_SCALE,
  });
}

export function rope(builder: MeshBuilder, style: VertexStyle, from: Vec3, to: Vec3): void {
  sparSolid(builder, style, {
    from,
    to,
    radiusFrom: ROPE_RADIUS,
    radiusTo: ROPE_RADIUS,
    sides: ROPE.sides,
    painterWidthScale: ROPE_PAINTER_WIDTH_SCALE,
  });
}

/* A shroud led aft at least tan(BRACE.max) metres for every metre out from the centreline lies
   wholly abaft a sail braced that far, so the lee shrouds clear the sail and yard at any brace. */
function shroudAft(mast: Mast, stations: readonly HullStation[], length: number): number {
  const reach = Math.tan(BRACE.max);
  const outboard = (aft: number): number =>
    SHROUD.inboard * gunwaleHalfWidth(stationAtX(stations, mast.x + aft, length));
  let aft = SHROUD.minAft;
  while (aft < reach * outboard(aft)) aft += SHROUD.step;
  return aft;
}

/* Shrouds run from inside the hull, just below the gunwale, to inside the masthead. */
export function buildShrouds(
  builder: MeshBuilder,
  style: VertexStyle,
  mast: Mast,
  stations: readonly HullStation[],
  length: number
): void {
  const first = shroudAft(mast, stations, length);
  for (const side of [1, -1]) {
    [first, first + SHROUD.spread].forEach((aft, i) => {
      const station = stationAtX(stations, mast.x + aft, length);
      const headY = mast.deck + SHROUD.head * mast.height - i * SHROUD.drop;
      const base: Vec3 = [
        station.x,
        station.gunwale - SHROUD.sink,
        side * SHROUD.inboard * gunwaleHalfWidth(station),
      ];
      rope(builder, style, base, [mast.x, headY, (side * mastRadiusAt(mast, headY)) / 2]);
    });
  }
}

/** Where a forestay without a bowsprit comes down: inside the stem, just below its head. */
export function bowPoint(template: ShipTemplate, stations: readonly HullStation[]): Vec3 {
  return [-template.length / 2 + FORESTAY.bowInset, bowTopHeight(stations) - FORESTAY.sink, 0];
}
